/**
 * ImpersonateHandoff
 *
 * Platform Admins land here in a new tab after clicking Impersonate on the
 * admin console. The impersonation JWT is passed as a URL hash fragment so
 * it is never sent to the server. This page:
 *   1. Reads the hash, extracts the token.
 *   2. Stores it in sessionStorage (tab-isolated — admin's localStorage is untouched).
 *   3. Strips the token from the URL (history.replaceState).
 *   4. Invalidates the React-Query cache so /api/auth/me is re-fetched.
 *   5. Navigates to /dashboard as the impersonated tenant user.
 *
 * If no token is present (direct navigation), redirects to /login.
 */
import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { setImpersonationToken } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";

export default function ImpersonateHandoff() {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const hash = window.location.hash; // e.g. "#t=eyJhbGci..."
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const token = params.get("t");

    if (!token) {
      setFailed(true);
      return;
    }

    // Store in sessionStorage — tab-isolated, survives reload, gone on tab close.
    setImpersonationToken(token);

    // Remove token from browser history / URL bar immediately.
    history.replaceState(null, "", "/impersonate");

    // Force a full re-fetch of /api/auth/me with the new session token.
    queryClient.clear();

    setReady(true);
  }, []);

  if (failed) return <Redirect to="/login" />;
  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-sm text-muted-foreground animate-pulse">
          Starting impersonation session…
        </p>
      </div>
    );
  }
  return <Redirect to="/" />;
}
